import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-[#E5B33E]" style={{ fontFamily: 'Manrope' }}>
              Privacy Policy
            </CardTitle>
            <p className="text-sm text-muted-foreground">Last Updated: January 28, 2025</p>
          </CardHeader>
          <CardContent className="prose prose-invert max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">1. Introduction</h2>
              <p className="text-muted-foreground">
                MudraFX ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our currency conversion application and website.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">2. Information We Collect</h2>
              <h3 className="text-lg font-medium text-foreground mb-2">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Conversion History:</strong> Currency pairs, amounts, and timestamps of conversions you perform</li>
                <li><strong>Favorites:</strong> Saved currency pairs for quick access</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our application</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mb-2 mt-4">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Device Information:</strong> Browser type, operating system, device type</li>
                <li><strong>IP Address:</strong> For general location and security purposes</li>
                <li><strong>Cookies:</strong> Small data files stored on your device for functionality and analytics</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">3. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-2">We use the collected information to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Provide and maintain currency conversion services</li>
                <li>Display your conversion history and saved favorites</li>
                <li>Improve our application and user experience</li>
                <li>Analyze usage patterns and trends</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">4. Data Storage and Security</h2>
              <p className="text-muted-foreground">
                Your data is stored securely in our MongoDB database. We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">5. Third-Party Services</h2>
              <h3 className="text-lg font-medium text-foreground mb-2">5.1 Exchange Rate Data</h3>
              <p className="text-muted-foreground mb-2">
                We use the following third-party services to provide exchange rate data:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>ExchangeRate-API:</strong> For real-time currency rates</li>
                <li><strong>Frankfurter API:</strong> For historical exchange rate data</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mb-2 mt-4">5.2 Analytics</h3>
              <p className="text-muted-foreground">
                We may use analytics services to understand how users interact with our application. These services may collect device information and usage patterns.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">6. Your Rights (GDPR & CCPA Compliance)</h2>
              <p className="text-muted-foreground mb-2">You have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Object:</strong> Object to processing of your personal data</li>
                <li><strong>Opt-out:</strong> Opt-out of data collection and processing</li>
              </ul>
              <p className="text-muted-foreground mt-3">
                To exercise these rights, please contact us at <a href="mailto:support@mudrafx.app" className="text-primary hover:underline">support@mudrafx.app</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">7. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. Conversion history and favorites are stored until you choose to delete them.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">8. Children's Privacy</h2>
              <p className="text-muted-foreground">
                MudraFX is not intended for users under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">9. International Data Transfers</h2>
              <p className="text-muted-foreground">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with applicable data protection laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">10. Changes to This Privacy Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any changes by updating the "Last Updated" date at the top of this policy. Continued use of MudraFX after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">11. Contact Us</h2>
              <p className="text-muted-foreground">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="text-primary mt-2">
                Email: <a href="mailto:support@mudrafx.app" className="hover:underline">support@mudrafx.app</a>
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}